import React, { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { Select, MenuItem } from '@mui/material'
import { courseApiSlice } from '../../../../features/course/courseSlice'
import { courseSubjectApiSlice } from '../../../../features/course/courseSubjectSlice'
import { topicApiSlice } from '../../../../features/topic/topicSlice'
import { H3 } from '../../../../layouts/TextStyles'
import FormControl from '@mui/material/FormControl'
import { Calendar, Whisper, Popover } from 'rsuite'
import 'rsuite/dist/rsuite.min.css'
import styled from '@emotion/styled'

export function CalendarTimetable() {
    const [isCompact, setIsCompact] = useState(false)
    const { formatMessage } = useIntl()
    const { data: courses = [] } = courseApiSlice.useFetchAllUserCoursesQuery(
        undefined,
        {
            refetchOnMountOrArgChange: true,
        },
    )

    const [selectedCourseIndex, setSelectedCourseIndex] = useState<
        number | null
    >(0)
    const [selectedSubjectIndex, setSelectedSubjectIndex] = useState<
        number | null
    >(0)

    const selectedCourseId =
        selectedCourseIndex >= 0 && selectedCourseIndex < courses.length
            ? courses[selectedCourseIndex].userCourseEnrollId
            : null

    const { data: subjects = [] } =
        courseSubjectApiSlice.useFetchAllStudentSubjectsQuery(selectedCourseId)

    const selectedSubjectTitle =
        selectedSubjectIndex >= 0 && selectedSubjectIndex < subjects.length
            ? subjects[selectedSubjectIndex].userSubjectEnrollId
            : null

    const { data: topics = [] } =
        topicApiSlice.useGetStudentTopicsBySubjectQuery(selectedSubjectTitle)

    const events = topics.map((topic) => ({
        title: topic?.topicTitleKz,
        start: new Date(topic?.timetableStart),
        end: new Date(topic?.timetableEnd),
        index: topic?.courseTopicId,
    }))

    const colors = ['#BBE7F1', '#E8D6FE', '#D1E49C', '#FCCFB0', '#FAEDAB']

    const locale = isCompact
        ? {
              sunday: formatMessage({ id: 'shortNameSunday' }),
              monday: formatMessage({ id: 'shortNameMonday' }),
              tuesday: formatMessage({ id: 'shortNameTuesday' }),
              wednesday: formatMessage({ id: 'shortNameWednesday' }),
              thursday: formatMessage({ id: 'shortNameThursday' }),
              friday: formatMessage({ id: 'shortNameFriday' }),
              saturday: formatMessage({ id: 'shortNameSaturday' }),
              ok: formatMessage({ id: 'ok' }),
              today: formatMessage({ id: 'today' }),
              yesterday: formatMessage({ id: 'yesterday' }),
              hours: formatMessage({ id: 'hours' }),
              minutes: formatMessage({ id: 'minutes' }),
              seconds: formatMessage({ id: 'seconds' }),
              formattedMonthPattern: 'MMM yyyy',
              formattedDayPattern: 'dd MMM yyyy',
          }
        : {
              sunday: formatMessage({ id: 'sunday' }),
              monday: formatMessage({ id: 'monday' }),
              tuesday: formatMessage({ id: 'tuesday' }),
              wednesday: formatMessage({ id: 'wednesday' }),
              thursday: formatMessage({ id: 'thursday' }),
              friday: formatMessage({ id: 'friday' }),
              saturday: formatMessage({ id: 'saturday' }),
              ok: formatMessage({ id: 'ok' }),
              today: formatMessage({ id: 'today' }),
              yesterday: formatMessage({ id: 'yesterday' }),
              hours: formatMessage({ id: 'hours' }),
              minutes: formatMessage({ id: 'minutes' }),
              seconds: formatMessage({ id: 'seconds' }),
              formattedMonthPattern: 'MMM yyyy',
              formattedDayPattern: 'dd MMM yyyy',
          }

    useEffect(() => {
        const handleResize = () => {
            setIsCompact(window.innerWidth <= 768)
        }

        handleResize()
        window.addEventListener('resize', handleResize)

        return () => {
            window.removeEventListener('resize', handleResize)
        }
    }, [])

    function renderCell(date) {
        const filteredEvents = events.filter((event) => {
            const eventStart = new Date(event.start)
            const eventEnd = new Date(event.end)
            return (
                eventStart.getFullYear() === date.getFullYear() &&
                eventStart.getMonth() === date.getMonth() &&
                eventStart.getDate() <= date.getDate() &&
                eventEnd.getDate() >= date.getDate()
            )
        })

        if (filteredEvents.length > 0) {
            return (
                <>
                    {filteredEvents.map((event, index) => (
                        <Whisper
                            placement="top"
                            key={index}
                            trigger="hover"
                            speaker={<Popover>{event.title}</Popover>}
                        >
                            <div
                                key={index}
                                style={{
                                    borderRadius: '4px',
                                    backgroundColor:
                                        colors[event.index % colors.length],
                                    color: '#404A51',
                                }}
                            >
                                <CalendarTextWrapper>
                                    {event.title}
                                </CalendarTextWrapper>
                            </div>
                        </Whisper>
                    ))}
                </>
            )
        }

        return null
    }

    return (
        <div style={{ height: '100%' }}>
            <HeaderWrapper>
                <Title>
                    <TitleLetter>
                        {formatMessage({ id: 'timetable' })}
                    </TitleLetter>
                </Title>
                <SelectWrapper>
                    <FormControl
                        sx={{ m: 0, minWidth: 120 }}
                        size="small"
                    >
                        <Select
                            value={selectedCourseIndex}
                            onChange={(event) => {
                                const selectedIndex = event.target
                                    .value as number
                                setSelectedCourseIndex(selectedIndex)
                            }}
                        >
                            {courses.map((course, index) => (
                                <MenuItem
                                    key={course.userCourseEnrollId}
                                    value={index}
                                >
                                    {course.titleKz}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl
                        sx={{ m: 0, minWidth: 120 }}
                        size="small"
                    >
                        <Select
                            value={selectedSubjectIndex}
                            onChange={(event) => {
                                const selectedIndex = event.target
                                    .value as number
                                setSelectedSubjectIndex(selectedIndex)
                            }}
                        >
                            {subjects.map((subject, index) => (
                                <MenuItem
                                    key={subject.userSubjectEnrollId}
                                    value={index}
                                >
                                    {subject.titleKz}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </SelectWrapper>
            </HeaderWrapper>

            <TimeTableCalendar
                style={{ flex: 1 }}
                events={events}
                locale={locale}
                compact={false}
                isoWeek={true}
                bordered={true}
                renderCell={renderCell}
            />
        </div>
    )
}

const SelectWrapper = styled.div`
    display: flex;
    gap: 20px;
    @media (max-width: 768px) {
        gap: 12px;
        width: 100%;
        justify-content: flex-start;
    }
    @media (max-width: 425px) {
        gap: 6px;
        width: 100%;
        justify-content: flex-start;
    }
`

const Title = styled.div`
    margin-bottom: 20px;
    text-align: left;
    display: flex;
    align-items: center;
    @media (max-width: 768px) {
        margin-bottom: 0px;
    }
`
const TimeTableCalendar = styled(Calendar)`
    .rs-calendar-body {
        padding: 4px 0px 0px;
    }
    .rs-calendar-header {
        padding-left: 0;
        padding-right: 0;
    }
    .rs-calendar-table-cell-day {
        display: block;
        margin-top: 8px;
        margin-bottom: 12px;
        text-align: right;
        padding-right: 2px;
    }
    .rs-calendar-table-cell-content {
        padding: 2px;
    }
    .rs-calendar-month-dropdown-list {
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: space-around;
        @media (max-width: 425px) {
            display: block;
            min-width: 200px;
            height: auto;
            width: auto;
        }
        @media (max-width: 320px) {
            min-width: 120px;
        }
    }

    .rs-calendar-table-cell-is-today .rs-calendar-table-cell-day {
        margin-right: 0 !important;
        text-align: center;
    }

    .rs-calendar-table-header-cell {
        display: table-cell;
        padding: 1px 0;
        padding-left: 5px;
        text-align: left;
        vertical-align: middle;
        width: 100%;
        white-space: nowrap;
        overflow: hidden;
    }
`

const HeaderWrapper = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    @media (max-width: 768px) {
        flex-direction: column;
        align-items: start;
        margin-bottom: 8px;
        gap: 16px;
    }
`
const TitleLetter = styled(H3)`
    @media (max-width: 768px) {
        font-size: 24px;
        line-height: 120%;
    }
`
const CalendarTextWrapper = styled.p`
    white-space: nowrap;
    overflow: hidden;
    padding: 4px;
    text-overflow: ellipsis;
    @media (max-width: 768px) {
        font-size: 12px;
        line-height: 120%;
    }
`
